/**
 * Detects transient browser network failures that are not actionable bugs.
 *
 * These mirror the `ignoreErrors` list in `src/lib/sentry.ts`. Some call sites
 * report a custom Sentry message (e.g. "Viewer manifest load failed",
 * NODE-EXPRESS-E) while carrying the real browser error in `extra`. Because
 * `ignoreErrors` only matches the event message/exception value, those custom
 * reports bypass it. Use this helper to suppress or downgrade reporting when
 * the underlying cause is a known network failure.
 */

const NETWORK_ERROR_PATTERNS = [
  'load failed',
  'failed to fetch',
  'networkerror',
  'aborterror',
  'a network error occurred',
] as const;

/**
 * Returns true when the message matches a known transient network failure.
 * Non-string, empty, or whitespace-only input returns false.
 */
export function isNetworkErrorMessage(message: string | null | undefined): boolean {
  if (typeof message !== 'string') {
    return false;
  }

  const normalized = message.trim().toLowerCase();
  if (normalized.length === 0) {
    return false;
  }

  return NETWORK_ERROR_PATTERNS.some((pattern) => normalized.includes(pattern));
}
